/**
 * Travel Vault PIN protection.
 *
 * Storage:
 *   - localStorage `tiplingo_vault_pin_v1` = SHA-256 hex of the 4-digit PIN
 *   - sessionStorage `tiplingo_vault_unlocked_v1` = "1" while the Vault is
 *     unlocked for the current browser session.
 *
 * Design:
 *   - PIN is local-only (per device) to keep the flow simple and to support
 *     anonymous users. Re-installs / cache clears reset it.
 *   - We never store the raw PIN. SHA-256 over a fixed salt is enough for a
 *     4-digit PIN on a device the user already controls.
 *   - "Unlocked" lasts only for the current session (sessionStorage) — closing
 *     the app re-locks the Vault.
 */
const PIN_KEY = "tiplingo_vault_pin_v1";
const UNLOCK_KEY = "tiplingo_vault_unlocked_v1";
const SALT = "tiplingo::vault::v1";

async function sha256Hex(text) {
  const enc = new TextEncoder().encode(text);
  const buf = await crypto.subtle.digest("SHA-256", enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export function isValidPin(pin) {
  return typeof pin === "string" && /^\d{4}$/.test(pin);
}

export function hasPin() {
  try {
    return !!localStorage.getItem(PIN_KEY);
  } catch {
    return false;
  }
}

export async function setPin(pin) {
  if (!isValidPin(pin)) throw new Error("PIN must be 4 digits");
  const h = await sha256Hex(SALT + pin);
  localStorage.setItem(PIN_KEY, h);
  // Lock immediately so the next visit to the Vault requires the PIN.
  sessionStorage.removeItem(UNLOCK_KEY);
}

export function clearPin() {
  try {
    localStorage.removeItem(PIN_KEY);
    sessionStorage.removeItem(UNLOCK_KEY);
  } catch (e) {
    console.error("clearPin: storage unavailable", e);
  }
}

export async function verifyPin(pin) {
  if (!isValidPin(pin)) return false;
  const stored = localStorage.getItem(PIN_KEY);
  if (!stored) return true; // no PIN set → always unlocked
  const h = await sha256Hex(SALT + pin);
  return h === stored;
}

export function isUnlocked() {
  if (!hasPin()) return true;
  try {
    return sessionStorage.getItem(UNLOCK_KEY) === "1";
  } catch {
    return false;
  }
}

export function unlock() {
  try {
    sessionStorage.setItem(UNLOCK_KEY, "1");
  } catch (e) {
    console.error("vault unlock: sessionStorage unavailable", e);
  }
}

export function lock() {
  try {
    sessionStorage.removeItem(UNLOCK_KEY);
  } catch (e) {
    console.error("vault lock: sessionStorage unavailable", e);
  }
}
